import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ClientTestimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      quote: "RCEG AI Ventures transformed our data analytics capabilities, resulting in a 40% increase in operational efficiency. Their AI models are incredibly accurate and have revolutionized our decision-making process.",
      author: "Sarah Chen",
      position: "CTO",
      company: "TechCorp Industries",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      companyLogo: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=120&h=40&fit=crop",
      rating: 5,
      project: "AI Analytics Platform"
    },
    {
      id: 2,
      quote: "The cybersecurity solutions implemented by RCEG have given us complete peace of mind. Their proactive approach to threat detection has prevented multiple potential breaches.",
      author: "Michael Rodriguez",
      position: "CISO",
      company: "SecureBank Financial",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      companyLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=40&fit=crop",
      rating: 5,
      project: "Enterprise Security Suite"
    },
    {
      id: 3,
      quote: "Working with RCEG on our AI engineering project was exceptional. They delivered a custom machine learning solution that exceeded our expectations and timeline.",
      author: "Emily Watson",
      position: "VP of Innovation",
      company: "InnovateLabs",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      companyLogo: "https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=120&h=40&fit=crop",
      rating: 5,
      project: "Custom ML Platform"
    },
    {
      id: 4,
      quote: "The software development team at RCEG built us a scalable platform that handles millions of transactions daily. Their technical expertise is unmatched.",
      author: "David Park",
      position: "Head of Engineering",
      company: "ScaleUp Solutions",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      companyLogo: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=120&h=40&fit=crop",
      rating: 5,
      project: "Enterprise Platform"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-20 bg-gradient-to-br from-muted/30 to-primary/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="MessageSquare" size={16} />
            <span>Client Success Stories</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Trusted by Industry Leaders
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            See how we've helped enterprise clients achieve remarkable results through our AI and technology solutions.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="relative">
          <div className="bg-white rounded-2xl shadow-elevation border border-border p-8 lg:p-12 mb-8">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              {/* Quote */}
              <div className="lg:col-span-2">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Quote" size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-1 mb-3">
                      {[...Array(currentTestimonial.rating)].map((_, i) => (
                        <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                      ))}
                    </div>
                    <blockquote className="text-lg lg:text-xl text-foreground leading-relaxed font-medium">
                      "{currentTestimonial.quote}"
                    </blockquote>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <Image
                    src={currentTestimonial.avatar}
                    alt={currentTestimonial.author}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-foreground text-lg">
                      {currentTestimonial.author}
                    </div>
                    <div className="text-muted-foreground">
                      {currentTestimonial.position}
                    </div>
                    <div className="text-primary font-medium">
                      {currentTestimonial.company}
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="bg-muted/50 rounded-xl p-6">
                <div className="text-center">
                  <Image
                    src={currentTestimonial.companyLogo}
                    alt={`${currentTestimonial.company} logo`}
                    className="h-12 mx-auto mb-4 object-contain"
                  />
                  <div className="text-sm text-muted-foreground mb-2">Project</div>
                  <div className="font-semibold text-foreground">
                    {currentTestimonial.project}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            {/* Dots */}
            <div className="flex items-center space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-primary w-8' :'bg-border hover:bg-primary/30'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            {/* Arrow Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={prevSlide}
                className="w-10 h-10 rounded-full bg-white border border-border hover:border-primary hover:bg-primary hover:text-white transition-all flex items-center justify-center"
                aria-label="Previous testimonial"
              >
                <Icon name="ChevronLeft" size={18} />
              </button>
              <button
                onClick={nextSlide}
                className="w-10 h-10 rounded-full bg-white border border-border hover:border-primary hover:bg-primary hover:text-white transition-all flex items-center justify-center"
                aria-label="Next testimonial"
              >
                <Icon name="ChevronRight" size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Client Logos */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="text-center mb-8">
            <p className="text-muted-foreground font-medium">Trusted by 50+ Enterprise Clients</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center opacity-60">
            {[
              "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=120&h=40&fit=crop",
              "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=40&fit=crop",
              "https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=120&h=40&fit=crop",
              "https://images.unsplash.com/photo-1551434678-e076c223a692?w=120&h=40&fit=crop",
              "https://images.unsplash.com/photo-1560472355-536de3962603?w=120&h=40&fit=crop",
              "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=40&fit=crop"
            ].map((logo, index) => (
              <div key={index} className="flex items-center justify-center">
                <Image
                  src={logo}
                  alt={`Client ${index + 1}`}
                  className="h-8 object-contain hover:opacity-100 transition-opacity"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientTestimonials;